import { getRemoteParticipants } from '../base/participants/functions';
import { parseURLParams } from '../base/util/parseURLParams';
import { DEEPAI_THEMES, DEFAULT_THEME, STORE_NAME, THEME_STYLE, THEME_STYLE_ELEMENT_ID } from './constants';
/**
 * Normalizes a raw stored theme value into the effective theme id.
 *
 * - `'none'` → `''` (explicit "off" / plain look — used by the
 *   `?deepaiTheme=none` URL override so you can A/B the theme vs the plain UI).
 * - empty / undefined → the default theme (there is no blank option anymore).
 * - any other value → itself.
 *
 * @param {string} [theme] - The raw stored theme value.
 * @returns {string}
 */
export function normalizeTheme(theme) {
    if (theme === 'none') {
        return '';
    }
    return theme || DEFAULT_THEME;
}
/**
 * Reads a theme override from the URL search params (`?deepaiTheme=<id>`).
 * Returns the value to store (a valid theme id, or `'none'` for the plain look),
 * or `undefined` when there is no (valid) override so the persisted theme wins.
 *
 * @returns {string | undefined}
 */
export function getThemeFromUrl() {
    if (typeof window === 'undefined') {
        return undefined;
    }
    let raw;
    try {
        raw = parseURLParams(window.location.href, true, 'search').deepaiTheme;
    }
    catch (e) {
        return undefined;
    }
    if (raw === undefined) {
        return undefined;
    }
    const v = String(raw).toLowerCase();
    if (v === 'none' || v === 'off' || v === 'plain' || v === 'default' || v === '') {
        return 'none';
    }
    if (DEEPAI_THEMES.some(t => t.value === v)) {
        return v;
    }
    // Unknown value → ignore the override and keep whatever is persisted.
    return undefined;
}
/**
 * Returns the currently selected DeepAI theme id from redux state.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {string}
 */
export function getDeepaiTheme(state) {
    return normalizeTheme(state[STORE_NAME]?.theme);
}
/**
 * Returns the AI participant (the one that advertised deepaiRole=ai), if any.
 *
 * @param {IReduxState} state - The redux state.
 * @returns {IParticipant | undefined}
 */
export function getAiParticipant(state) {
    for (const p of getRemoteParticipants(state).values()) {
        if (p.deepaiRole === 'ai') {
            return p;
        }
    }
    return undefined;
}
/**
 * Ensures the <style> element holding all theme variable blocks is present in
 * the document head. Idempotent.
 *
 * @returns {void}
 */
export function ensureThemeStylesheet() {
    if (typeof document === 'undefined') {
        return;
    }
    if (document.getElementById(THEME_STYLE_ELEMENT_ID)) {
        return;
    }
    const style = document.createElement('style');
    style.id = THEME_STYLE_ELEMENT_ID;
    style.textContent = THEME_STYLE;
    document.head.appendChild(style);
}
/**
 * Applies a theme to the DOM by toggling the `data-theme` attribute on the
 * document root. An empty theme removes the attribute (default look).
 *
 * @param {string} theme - The theme id.
 * @returns {void}
 */
export function applyThemeToDom(theme) {
    if (typeof document === 'undefined') {
        return;
    }
    ensureThemeStylesheet();
    const root = document.documentElement;
    if (theme) {
        root.setAttribute('data-theme', theme);
    }
    else {
        root.removeAttribute('data-theme');
    }
}
